import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import LanguageStore from '../store/LanguageStore';
import VideoService from '../services/VideoService';
import VideosTable from '../components/VideosTable/VideosTable';
import JsonLd from '../components/JsonLd/JsonLd';
import { createVideoListSchema } from '../utils/jsonLdSchemas';
import './videos-page.scss';

const VideosPage = observer(() => {
    const t = LanguageStore.t;
    const { language } = LanguageStore;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [videos, setVideos] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        totalPages: 1,
        totalItems: 0
    });
    const [sortBy, setSortBy] = useState('viewCount');
    const [sortOrder, setSortOrder] = useState('DESC');

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                setLoading(true);
                const data = await VideoService.getVideoListWithSorting(
                    pagination.page,
                    pagination.limit,
                    sortBy,
                    sortOrder
                );
                setVideos(data.items);
                setPagination(data.pagination);
                setError(null);
            } catch (err) {
                console.error('Error fetching videos:', err);
                console.error('Error response:', err.response);
                const errorMessage = err.response?.data?.error 
                    || err.response?.data?.message 
                    || err.message 
                    || 'Failed to load videos';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchVideos();
    }, [pagination.page, pagination.limit, sortBy, sortOrder]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            // Переключаем порядок сортировки
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            // Новая колонка для сортировки
            setSortBy(field);
            setSortOrder('DESC');
        }
        // Сбрасываем на первую страницу при изменении сортировки
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    if (loading && videos.length === 0) {
        return (
            <div className="videos-page">
                <div className="container">
                    <div className="videos-page__loading">
                        <div className="videos-page__spinner"></div>
                        <p>{t('loading') || 'Loading...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && videos.length === 0) {
        return (
            <div className="videos-page">
                <div className="container">
                    <div className="videos-page__error">
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const siteUrl = window.location.origin;
    const pageTitle = language === 'ru' ? 'Список видео' : 'Videos List';
    const description = language === 'ru' 
        ? 'Список трендовых видео на YouTube с детальной статистикой'
        : 'List of trending YouTube videos with detailed statistics';
    
    const videoListSchema = createVideoListSchema(siteUrl, pageTitle, description, language);

    return (
        <div className="videos-page">
            <JsonLd data={videoListSchema} id="json-ld-video-list" />
            <article className="container">
                <h1 className="videos-page__title">
                    {pageTitle}
                </h1>

                <VideosTable
                    videos={videos}
                    pagination={pagination}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    onPageChange={handlePageChange}
                    loading={loading}
                />
            </article>
        </div>
    );
});

export default VideosPage;

